import React, { useState } from "react";
import { useTranslation } from "../../../../../i18n/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
  faClockRotateLeft,
  faSms,
} from "@fortawesome/free-solid-svg-icons";
import RefreshButton from "../../../common/RefreshButton";
import SmsAnalysisAPI from "../../../services/SmsAnalysisAPI";
import { formatDateOnly, formatTimeOnly } from "../../../utils/DateTimeUtil";
import { FontAwesomeIcon as FAIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import "../css/SmsAnalysisSimple.css";

const SmsAnalysisResultsSimple = ({
  rawAnalyses,
  selectedAnalysis,
  onAnalysisClick,
  onRefresh,
  userUuid,
  onError,
  onAnalysisDeleted,
  isMobile = false,
  showMobileOriginalMessage = false,
  onToggleMobileOriginalMessage = () => {},
  onNavigateToManual,
}) => {
  const [analyses, setAnalyses] = useState(rawAnalyses || []);
  const { t } = useTranslation();

  React.useEffect(() => {
    setAnalyses(rawAnalyses || []);
  }, [rawAnalyses]);

  const formatDate = (date) => {
    if (!date) return "Unknown";
    return formatDateOnly(date, "short");
  };

  const formatTime = (date) => {
    if (!date) return "";
    return formatTimeOnly(date, true);
  };

  const getConfidenceDescription = (confidence, isScam) => {
    const percentage = Math.round(confidence * 100);
    if (isScam) {
      if (confidence >= 0.8)
        return t("sms.confidence.veryLikelyScam").replace("{pct}", percentage);
      if (confidence >= 0.5)
        return t("sms.confidence.likelyScam").replace("{pct}", percentage);
      return t("sms.confidence.possiblyScam").replace("{pct}", percentage);
    } else {
      if (confidence >= 0.8)
        return t("sms.confidence.veryLikelySafe").replace("{pct}", percentage);
      if (confidence >= 0.5)
        return t("sms.confidence.likelySafe").replace("{pct}", percentage);
      return t("sms.confidence.possiblySafe").replace("{pct}", percentage);
    }
  };

  const handleDelete = async (analysis, event) => {
    event.stopPropagation();

    try {
      await SmsAnalysisAPI.deleteSmsAnalysis(analysis.id);

      // Check if the deleted analysis is currently selected
      if (selectedAnalysis && selectedAnalysis.id === analysis.id) {
        onAnalysisClick(null);
      }

      onAnalysisDeleted(analysis.id);
    } catch (error) {
      console.error("Error deleting SMS analysis:", error);
      onError("Failed to delete SMS analysis. Please try again.");
    }
  };

  return (
    <>
      <div className="common-section-header">
        <div className="results-header-content">
          <div className="results-header-top">
            <h3>
              <FontAwesomeIcon icon={faClockRotateLeft} />{" "}
              {t("sms.results.heading")}
            </h3>
            <RefreshButton
              onClick={onRefresh}
              title={t("sms.results.refreshTitle")}
            />
          </div>
          <div className="results-header-bottom">
            <p>
              {analyses.length === 0
                ? t("sms.results.noSmsFound")
                : (analyses.length === 1
                    ? t("sms.results.showingSingular")
                    : t("sms.results.showingPlural")
                  ).replace("{count}", analyses.length)}
            </p>
          </div>
        </div>
      </div>

      <div className="panel-content history-panel-content">
        {!analyses || analyses.length === 0 ? (
          <div className="empty-state-with-manual">
            <div className="coming-soon-message">
              <h3>
                <FontAwesomeIcon
                  icon={faSms}
                  className="fa-icon-margin-right-8"
                />
                {t("sms.empty.noAnalysisFound")}
              </h3>
              <p>{t("sms.empty.forwardToKinShield")}</p>
            </div>
            {onNavigateToManual && (
              <div className="manual-entry-cta">
                <span className="manual-entry-cta-divider">— or —</span>
                <button
                  type="button"
                  className="common-button manual-entry-cta-btn"
                  onClick={onNavigateToManual}
                >
                  <FAIcon icon={faEdit} className="icon-right-margin" />
                  {t("sms.empty.analyzeManuallyBtn")}
                </button>
              </div>
            )}
          </div>
        ) : (
          analyses.map((analysis) => {
            const isScam = analysis.analysis?.analysisResult?.isScam || false;
            const confidence =
              analysis.analysis?.analysisResult?.confidence || 0;
            const isSelected = selectedAnalysis?.id === analysis.id;

            return (
              <div
                key={analysis.id}
                className={`analysis-item-card ${
                  isSelected ? "selected" : ""
                } ${isScam ? "scam" : ""}`}
                onClick={() => onAnalysisClick(analysis)}
              >
                {/* Top row: Status badge and Delete button */}
                <div className="analysis-item-header">
                  <div
                    className={`status-badge ${isScam ? "scam" : "legitimate"}`}
                  >
                    {isScam ? (
                      <>
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="fa-icon-margin-right-4"
                        />
                        {t("sms.badge.scam")}
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="fa-icon-margin-right-4"
                        />
                        {t("sms.badge.legitimate")}
                      </>
                    )}
                  </div>

                  <button
                    className="delete-button"
                    onClick={(e) => handleDelete(analysis, e)}
                    title={t("sms.card.deleteTitle")}
                  >
                    {t("sms.card.delete")}
                  </button>
                </div>

                {/* Message content row */}
                <div className="sms-message-content">
                  {analysis.messageContent?.substring(0, 150)}
                  {analysis.messageContent?.length > 150 ? "..." : ""}
                </div>

                {/* Bottom row: Confidence and Date */}
                <div className="analysis-item-footer">
                  <span className="confidence-info">
                    {getConfidenceDescription(confidence, isScam)}
                  </span>
                  <span className="date-info">
                    {formatDate(analysis.lastModified)}{" "}
                    {formatTime(analysis.lastModified)}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
};

export default SmsAnalysisResultsSimple;
